import { inject, Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { IServerSideGetRowsRequest, LoadSuccessParams } from 'ag-grid-community';
import { map, tap } from 'rxjs';
import { CompanyRef } from 'src/app/core/models/object-ref.models';
import { NomItem, TimeInfo } from 'src/app/core/models/core.models';
import { UtilsService } from 'src/app/core/services/utils.service';
import { HEADERS } from '../api-headers';
import { HttpClient } from '@angular/common/http';

import {
  OUTPUT_SETTINGS_GET_DEFAULT_OUTPUT_SETTINGS,
  OUTPUT_SETTINGS_GET_RECORD,
  OUTPUT_SETTINGS_POST_SAVE_OUTPUT_SETTINGS_STATE,
  OUTPUT_SETTINGS_POST_SAVE,
  OUTPUT_SETTINGS_POST_ADMIN_LIST,
  OUTPUT_SETTINGS_POST_ADMIN_UPDATE,
  OUTPUT_SETTINGS_POST_ADMIN_COPY,
  OUTPUT_SETTINGS_DELETE_ADMIN_DELETE,
} from '../endpoints/output-settings.endpoints';

import { toQueryParams } from 'src/app/api/api-utils';

import {
  DataObject,
  OutputSettingsType,
} from 'src/app/core/models/core.models';
import { OutputSettings } from 'src/app/core/models/output-settings.models';

import { Observable } from 'rxjs';

/**
 * One row of Admin -> Output Settings (POST /output_settings/admin_list, SEN-5223
 * section 6): the active named lists (type 2) and company defaults (type 1) of a
 * company; custom per-object settings (type 3) stay in their owner's dialog.
 */
export interface AdminOutputSettings {
  id: number;
  output_settings_type: NomItem;
  company_id: string;
  company: CompanyRef | null;
  title: string;
  description: string;
  timezone_id: number | null;
  format_ids: number[];
  /** Objects linked to the row; a delete is refused (409) while it is above 0. */
  count_used: number;
  time_created: TimeInfo;
}

/** Body of POST /output_settings/admin_update: only the fields to change. */
export interface AdminOutputSettingsUpdate {
  id: number;
  title?: string;
  description?: string;
}

/** Body of POST /output_settings/admin_copy. */
export interface AdminOutputSettingsCopy {
  id: number;
  /** Target company; another company than the source is Global Admin only. */
  company_id?: string;
  /** Defaults to the source title suffixed " (copy)" within the same company. */
  title?: string;
}

// TODO: We will pass typized objects to the services containing all inputs
@Injectable({
  providedIn: 'root',
})
export class OutputSettingsApiService {
  private readonly http = inject(HttpClient);
  private readonly utils = inject(UtilsService);

  fetchObjectOutputSettings(
    objectId: string,
    objectClassId: number,
  ): Observable<OutputSettings> {
    const params = toQueryParams({
      object_id: objectId,
      object_class_id: objectClassId,
    });

    return this.http.get<OutputSettings>(
      OUTPUT_SETTINGS_GET_DEFAULT_OUTPUT_SETTINGS,
      {
        params,
      },
    );
  }

  getOutputSettingsRecord(
    outputSettingsId: number,
    objectId: string,
    objectClassId: number,
  ): Observable<OutputSettings> {
    const params = toQueryParams({
      output_settings_id: outputSettingsId,
      object_id: objectId,
      object_class_id: objectClassId,
    });

    return this.http.get<OutputSettings>(OUTPUT_SETTINGS_GET_RECORD, {
      params,
      // Odd for a GET, but this is the header the endpoint has always received.
      headers: HEADERS.formUrlEncodedNoCharset,
    });
  }

  saveOutputSettings(
    objectId: string,
    objectClassId: number,
    outputSettings: OutputSettings,
  ): Observable<OutputSettings> {
    return this.http.post<OutputSettings>(OUTPUT_SETTINGS_POST_SAVE, {
      object_id: objectId,
      object_class_id: objectClassId.toString(),
      data: outputSettings,
    });
  }

  saveObjectExportSettingsState(
    contextObject: DataObject,
    settingsType: OutputSettingsType,
    settings: any,
  ): Observable<any> {
    const httpParams = {
      object_id: contextObject.id,
      object_class_id: contextObject.object_class_id,
      settings_type: settingsType,
      settings: settings,
    };

    return this.http.post<any>(
      OUTPUT_SETTINGS_POST_SAVE_OUTPUT_SETTINGS_STATE,
      httpParams,
    );
  }

  /**
   * POST /output_settings/admin_list[?company_id=...] - the Solr-backed admin grid
   * (ag-grid request body in, envelope out). `companyId` is the Global Admin's
   * switch; omitted = the caller's active company.
   */
  getAdminOutputSettingsAgGridModel(
    gridParams: IServerSideGetRowsRequest,
    companyId?: string,
  ): Observable<LoadSuccessParams> {
    return this.http.post<LoadSuccessParams>(
      OUTPUT_SETTINGS_POST_ADMIN_LIST,
      gridParams,
      { params: toQueryParams({ company_id: companyId || undefined }) },
    );
  }

  /** POST /output_settings/admin_list?output=csv - see ClientAdminApiService.exportUsersCsv(). */
  exportAdminOutputSettingsCsv(
    request: IServerSideGetRowsRequest,
    companyId?: string,
  ): Observable<void> {
    return this.http
      .post(OUTPUT_SETTINGS_POST_ADMIN_LIST, request, {
        params: toQueryParams({
          output: 'csv',
          company_id: companyId || undefined,
        }),
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        tap((response: HttpResponse<Blob>) => {
          const fileName = this.utils.getFileNameFromDisposition(
            response.headers.get('content-disposition'),
            'output-settings.csv',
          );
          this.utils.downloadBlob(response.body!, fileName);
        }),
        map(() => undefined),
      );
  }

  /** POST /output_settings/admin_update - rename / describe; 400 on an empty title. Returns the row. */
  adminUpdateOutputSettings(
    update: AdminOutputSettingsUpdate,
  ): Observable<AdminOutputSettings> {
    return this.http.post<AdminOutputSettings>(
      OUTPUT_SETTINGS_POST_ADMIN_UPDATE,
      update,
    );
  }

  /**
   * DELETE /output_settings/admin_delete/{id} - soft delete. 409 while objects
   * still point at the row (`count_used` > 0) and always for a company default.
   */
  adminDeleteOutputSettings(outputSettingsId: number): Observable<unknown> {
    return this.http.delete(
      OUTPUT_SETTINGS_DELETE_ADMIN_DELETE(outputSettingsId),
    );
  }

  /** POST /output_settings/admin_copy - a new named list. Returns the new row. */
  adminCopyOutputSettings(
    copy: AdminOutputSettingsCopy,
  ): Observable<AdminOutputSettings> {
    return this.http.post<AdminOutputSettings>(
      OUTPUT_SETTINGS_POST_ADMIN_COPY,
      copy,
    );
  }
}
